import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Components
import Sidebar from '../main/Sidebar';
import Topbar from '../main/Topbar';

// Utils
import { getUserInfo } from '../../utils/auth';
import { getUserDetail, updateUser, UserDetail } from '../../utils/user';
import { UserFormUtils, FormData, FormErrors } from '../../utils/userCommon';
import { CommonUtils } from '../../utils/commonUtils';
import { formatDateTimeFromApi } from '../../utils/dateTime';

import { 
  Save,
  ArrowLeft,
  Shield,
  Eye,
  EyeOff,
  User,
  RefreshCw,
  X,
  AlertCircle,
  History
} from 'lucide-react';
  
const UserUpdate: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('users');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormData>(UserFormUtils.getDefaultFormData());
  const [errors, setErrors] = useState<FormErrors>({});
  const [originalData, setOriginalData] = useState<FormData | null>(null);

  // API state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // User info state
  const [userInfo, setUserInfo] = useState<any>(null);
  const [userMetadata, setUserMetadata] = useState<{
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);

  // Check if screen is mobile size
  UserFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 사용자 정보 로드
  useEffect(() => {
    const info = UserFormUtils.loadUserInfo();
    if (info) {
      setUserInfo(info);
    }
  }, []);

  // 사용자 상세 정보 로드 (중복 호출 방지)
  const isFetchingRef = React.useRef(false);
  
  useEffect(() => {
    const loadUserDetail = async () => {
      if (!userId) {
        setLoadError('사용자 ID가 없습니다.');
        setIsLoading(false);
        return;
      }

      if (!userInfo) {
        setIsLoading(false);
        return;
      }

      // 이미 요청 중이면 중복 호출 방지
      if (isFetchingRef.current) {
        return;
      }

      try {
        isFetchingRef.current = true;
        setIsLoading(true);
        setLoadError(null);
        
        const accountCode = userInfo.accountCode;
        const user = await getUserDetail(accountCode, userId);

        // API 응답을 FormData 형태로 변환
        const userFormData: FormData = {
          userId: user.userId,
          password: '',
          confirmPassword: '',
          status: user.status,
          name: user.name,
          mfaType: user.mfaType || 'NONE',
          department: user.department || '',
          phoneNumber: user.phoneNumber || ''
        };
        
        setFormData(userFormData);
        setOriginalData(userFormData);
        
        // 메타데이터 저장
        setUserMetadata({
          createdAt: user.createdAt,
          createdBy: user.createdBy,
          updatedAt: user.updatedAt,
          updatedBy: user.updatedBy
        });
      } catch (error: any) {
        console.error('사용자 상세 정보 로드 오류:', error);
        // 에러 메시지 추출 (서버에서 보낸 메시지 우선 사용)
        const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '사용자 정보를 불러오는 중 오류가 발생했습니다.';
        console.log('에러 상세 정보:', {
          message: error?.message,
          status: error?.status,
          response: error?.response
        });
        setLoadError(displayMessage);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    if (userInfo) {
      loadUserDetail();
    }
  }, [userId, userInfo]);

  // Track changes
  useEffect(() => {
    if (originalData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(changed);
    }
  }, [formData, originalData]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    UserFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors = UserFormUtils.validateUpdateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    const getAccountCode = (): string | null => {
      return userInfo?.accountCode || null;
    };

    await UserFormUtils.handleUpdateSubmit(
      e,
      formData,
      validateForm,
      setIsSubmitting,
      setSubmitError,
      setHasChanges,
      setOriginalData,
      getAccountCode,
      userId!,
      updateUser,
      navigate,
      location
    );
  };

  // Reset form to original data
  const handleReset = () => {
    if (originalData) {
      setFormData({...originalData});
    }
    setErrors({});
    setHasChanges(false);
    setShowResetConfirm(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
          <p className="text-gray-600">사용자 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <X className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-red-900 mb-2">오류 발생</h3>
            <p className="text-red-700 mb-4">{loadError}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                다시 시도
              </button>
              <button
                onClick={() => navigate('/users')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                목록으로
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <Sidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        isProfileDropdownOpen={isProfileDropdownOpen}
        setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="사용자 수정"
          subtitle={`${formData.name || userId} 사용자 정보를 수정하세요`}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        />

        {/* Edit User Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            {/* Back Button and Status */}
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => {
                  const searchParams = new URLSearchParams(location.search);
                  const page = searchParams.get('page') || '0';
                  const size = searchParams.get('size') || '20';
                  navigate(`/users?page=${page}&size=${size}`);
                }}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                사용자 목록으로
              </button>
              
              {hasChanges && (
                <span className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  수정사항 있음
                </span>
              )}
            </div>

            {/* User Info Card */}
            {userMetadata && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                      {formData.name} ({formData.userId})
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center">
                        <History className="w-4 h-4 mr-2" />
                        생성: {formatDateTimeFromApi(userMetadata.createdAt) || userMetadata.createdAt} ({userMetadata.createdBy || '-'})
                      </div>
                      <div className="flex items-center">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        수정: {formatDateTimeFromApi(userMetadata.updatedAt) || userMetadata.updatedAt} ({userMetadata.updatedBy || '-'})
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-red-700">{submitError}</span>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Account Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  계정 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      아이디 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="userId"
                      value={formData.userId}
                      onChange={handleInputChange}
                      maxLength={20}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      placeholder="user123"
                    />
                    <p className="mt-1 text-xs text-gray-500">아이디는 수정할 수 없습니다.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                      <option value="suspended">정지</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      비밀번호 변경 (선택)
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        maxLength={20}
                        className={`w-full px-3 py-2 pr-10 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="변경하지 않으려면 비워두세요"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Eye className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
                    <p className="mt-1 text-xs text-gray-500">8-20자 입력 (변경하지 않으려면 비워두세요)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      비밀번호 확인
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        maxLength={20}
                        className={`w-full px-3 py-2 pr-10 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="비밀번호 재입력"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Eye className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  개인 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      사용자명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={50}
                      className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="홍길동"
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      부서
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      maxLength={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="영업팀"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      연락처
                    </label>
                    <input
                      type="text"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="010-1234-5678"
                    />
                    {errors.phoneNumber && <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p>}
                  </div>
                </div>
              </div>

              {/* Security Settings - Hidden for now */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hidden">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2" />
                  보안 설정
                </h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    이중 인증 방식
                  </label>
                  <select
                    name="mfaType"
                    value={formData.mfaType}
                    onChange={handleInputChange}
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="NONE">사용안함</option>
                    <option value="SMS">SMS 인증</option>
                    <option value="EMAIL">이메일 인증</option>
                    <option value="OTP">OTP (Google OTP)</option>
                  </select>
                  <p className="mt-2 text-xs text-gray-500">
                    이중 인증을 설정하면 로그인 시 추가 인증이 필요합니다.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={isSubmitting || !hasChanges}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      const searchParams = new URLSearchParams(location.search);
                      const page = searchParams.get('page') || '0';
                      const size = searchParams.get('size') || '20';
                      navigate(`/users?page=${page}&size=${size}`);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !hasChanges}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {isSubmitting ? '수정 중...' : '수정 저장'}
                  </button>
                </div>
              </div>
            </form>
            </div>
          </main>
        </div>

        {/* Reset Confirm Modal */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">변경사항 초기화</h3>
              <p className="text-gray-600 mb-6">
                모든 변경사항이 취소되고 원래 데이터로 복원됩니다. 계속하시겠습니까?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  초기화
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  export default UserUpdate;